import { Link } from "react-router-dom";
import { ExternalLink, HelpExpander, HelpExpanderGroup } from "./blocks";
import { bhfAllMonitorsUrl } from "./purchase-monitors";

// Content from "FAQ v1.1.docx". Questions are expanders so patients can scan the list.

const adhdTeamPhone = "0141 800 7809";
const strideBpUrl = "https://www.stridebp.org/bp-monitors/";

const choosingPage = "/help/adhd/choosing-a-blood-pressure-monitor";
const buyingPage = "/help/adhd/buying-a-blood-pressure-monitor";

const BhfLink = () => (
  <ExternalLink href={bhfAllMonitorsUrl}>British Heart Foundation website</ExternalLink>
);

// Both "how do I know" and "easiest way to buy" answer with the same two options.
const WaysToBuy = () => (
  <ul className="nhsuk-list nhsuk-list--bullet">
    <li>
      Buy one of the ones listed on the <BhfLink />
    </li>
    <li>
      Buy one of the suggested ones listed in{" "}
      <Link to={buyingPage}>Buying a Blood Pressure Monitor</Link>
    </li>
  </ul>
);

export const Faqs = () => (
  <>
    <h2>Before Monitoring</h2>
    <HelpExpanderGroup>
      <HelpExpander question="I don't have a blood pressure monitor">
        <p>
          You'll need a validated upper-arm blood pressure monitor to take part. Our{" "}
          <Link to={choosingPage}>Choosing a Blood Pressure Monitor</Link> page includes recommended
          models and where to buy them.
        </p>
      </HelpExpander>

      <HelpExpander question="How do I know if it's a validated monitor?">
        <p>
          You can check out the full list of validated monitors at{" "}
          <ExternalLink href={strideBpUrl}>STRIDE-BP</ExternalLink> website or:
        </p>
        <WaysToBuy />
      </HelpExpander>

      <HelpExpander question="What's the easiest way to buy a validated monitor?">
        <p>You can:</p>
        <WaysToBuy />
      </HelpExpander>

      <HelpExpander question="Can I use a wrist monitor?">
        <p>
          We recommend using a validated upper-arm blood pressure monitor, as these provide the most
          accurate readings and are recommended by the NHS.
        </p>
      </HelpExpander>

      <HelpExpander question="Do I need Bluetooth?">
        <p>
          No. Bluetooth is not required. You can simply enter your blood pressure and pulse readings
          manually into the Patient Portal.
        </p>
      </HelpExpander>

      <HelpExpander question="How often will I need to complete monitoring?">
        <p>
          Your ADHD Team will tell you how often monitoring is required. During this pilot,
          monitoring is typically requested once a month.
        </p>
      </HelpExpander>
    </HelpExpanderGroup>

    <h2>Completing Monitoring</h2>
    <HelpExpanderGroup>
      <HelpExpander question="How long does monitoring take?">
        <p>
          Most people complete their monitoring in less than 5 minutes once they have their blood
          pressure monitor ready.
        </p>
      </HelpExpander>

      <HelpExpander question="What information will I be asked to provide?">
        <p>
          You'll be asked to enter your blood pressure, pulse (heart rate) and complete a short
          side-effect questionnaire. You can also enter your weight, but this question is optional.
        </p>
      </HelpExpander>

      <HelpExpander question="Can I complete monitoring on my phone?">
        <p>Yes. The Patient Portal works on smartphones, tablets and computers.</p>
      </HelpExpander>

      <HelpExpander question="I can't log in">
        <p>
          Please check that you're using the link provided in your email and that your email address
          is correct. If you're still unable to access your account, please contact your ADHD Team on{" "}
          <a href={`tel:${adhdTeamPhone.replace(/\s/g, "")}`}>{adhdTeamPhone}</a>.
        </p>
      </HelpExpander>
    </HelpExpanderGroup>

    <h2>After Submitting</h2>
    <HelpExpanderGroup>
      <HelpExpander question="What happens after I submit my readings?">
        <p>
          Your readings are securely sent to your ADHD clinical team for review. If any further
          action is needed, your team will contact you.
        </p>
      </HelpExpander>

      <HelpExpander question="When will someone review my results?">
        <p>
          Your results will be reviewed by a member of your ADHD clinical team. If any follow-up is
          required, your team will contact you.
        </p>
      </HelpExpander>

      <HelpExpander question="The Patient Portal says one of my readings is outside the expected range">
        <p>
          Don't worry - one unusual reading does not necessarily mean there is a problem. If the
          Patient Portal advises you to contact your ADHD Team or CMHT, please follow that advice. If
          you feel unwell or are concerned about your symptoms, seek medical advice promptly.
        </p>
      </HelpExpander>
    </HelpExpanderGroup>

    <h2>General</h2>
    <HelpExpanderGroup>
      <HelpExpander question="What if I no longer want to take part?">
        <p>
          Taking part in the pilot is voluntary. If you no longer wish to participate, please contact
          your ADHD Team on{" "}
          <a href={`tel:${adhdTeamPhone.replace(/\s/g, "")}`}>{adhdTeamPhone}</a>.
        </p>
      </HelpExpander>
    </HelpExpanderGroup>
  </>
);

export default Faqs;
